/** Normalise the jsonb returned by program_stats / outreach_overview and format it. */

import type {
  DealStageRow,
  MailboxStatRow,
  OutreachOverview,
  PausedReason,
  ProgramKind,
  ProgramStatRow,
  SiteStatRow,
  StageCount,
  StatsTotals,
  WorkspaceStats,
} from './types'

type Json = Record<string, unknown>

function obj(v: unknown): Json {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : {}
}
function arr(v: unknown): Json[] {
  return Array.isArray(v) ? v.map(obj) : []
}
function num(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}
function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined) return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}
function str(v: unknown): string {
  return typeof v === 'string' ? v : v === null || v === undefined ? '' : String(v)
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null
}

const KINDS: ProgramKind[] = ['editorial', 'sales', 'other']

function normalizeTotals(t: Json): StatsTotals {
  return {
    sent_today: num(t.sent_today),
    sent_7d: num(t.sent_7d),
    sent_30d: num(t.sent_30d),
    sent: num(t.sent),
    replies: num(t.replies),
    bounces: num(t.bounces),
    optouts: num(t.optouts),
    reply_rate: numOrNull(t.reply_rate),
    bounce_rate: numOrNull(t.bounce_rate),
    queued: num(t.queued),
    due_today: num(t.due_today),
    suppressions: num(t.suppressions),
  }
}

export function normalizeStats(raw: unknown): WorkspaceStats {
  const r = obj(raw)
  const win = obj(r.window)
  const pipeline: WorkspaceStats['pipeline'] = {}
  const rawPipe = obj(r.pipeline)
  for (const kind of KINDS) {
    const rows = arr(rawPipe[kind]).map<StageCount>((x) => ({
      stage: str(x.stage),
      count: num(x.count),
    }))
    if (rows.length > 0) pipeline[kind] = rows
  }
  return {
    workspace_id: str(r.workspace_id),
    timezone: str(r.timezone) || 'UTC',
    window: { from: str(win.from), to: str(win.to) },
    totals: normalizeTotals(obj(r.totals)),
    by_program: arr(r.by_program).map<ProgramStatRow>((p) => ({
      program_id: str(p.program_id),
      name: str(p.name),
      kind: (KINDS as string[]).includes(str(p.kind)) ? (str(p.kind) as ProgramKind) : 'other',
      active: p.active === true,
      site_id: strOrNull(p.site_id),
      from_label: strOrNull(p.from_label),
      cap_today: num(p.cap_today),
      sent_today: num(p.sent_today),
      remaining: num(p.remaining),
      paused_reason: strOrNull(p.paused_reason) as PausedReason | null,
      sent_7d: num(p.sent_7d),
      sent_30d: num(p.sent_30d),
      replies: num(p.replies),
      bounces: num(p.bounces),
      optouts: num(p.optouts),
      queued: num(p.queued),
      due_today: num(p.due_today),
    })),
    by_site: arr(r.by_site).map<SiteStatRow>((s) => ({
      site_id: strOrNull(s.site_id),
      name: str(s.name) || 'No site',
      sent: num(s.sent),
      replies: num(s.replies),
      bounces: num(s.bounces),
      optouts: num(s.optouts),
    })),
    by_mailbox: arr(r.by_mailbox).map<MailboxStatRow>((m) => ({
      mailbox: str(m.mailbox) || '(none)',
      sent: num(m.sent),
      replies: num(m.replies),
      bounces: num(m.bounces),
    })),
    pipeline,
    deals_by_stage: arr(r.deals_by_stage).map<DealStageRow>((d) => ({
      stage: str(d.stage),
      count: num(d.count),
      value: num(d.value),
    })),
  }
}

export function normalizeOverview(raw: unknown): OutreachOverview {
  const r = obj(raw)
  const t = obj(r.totals)
  return {
    totals: {
      businesses: num(t.businesses),
      sent_today: num(t.sent_today),
      sent_7d: num(t.sent_7d),
      sent_30d: num(t.sent_30d),
      sent: num(t.sent),
      replies: num(t.replies),
      bounces: num(t.bounces),
      optouts: num(t.optouts),
      queued: num(t.queued),
      due_today: num(t.due_today),
      reply_rate: numOrNull(t.reply_rate),
      bounce_rate: numOrNull(t.bounce_rate),
    },
    businesses: arr(r.businesses).map((b) => ({
      workspace_id: str(b.workspace_id),
      name: str(b.name),
      kind: b.kind === 'personal' ? 'personal' : 'business',
      role: b.role === 'owner' || b.role === 'admin' ? b.role : 'member',
      stats: normalizeStats(b.stats),
    })),
  }
}

/** "16.7%" or an em dash when there is nothing to divide by. */
export function formatRate(rate: number | null): string {
  if (rate === null) return '—'
  return `${Number.isInteger(rate) ? rate : rate.toFixed(1)}%`
}

export function formatCount(n: number): string {
  return new Intl.NumberFormat('en-US').format(n)
}

export function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n)
}

export const PAUSED_LABELS: Record<PausedReason, string> = {
  inactive: 'Switched off',
  no_sequence: 'No sequence or steps',
  not_a_send_day: 'Not a send day',
  bounce_stop: 'Stopped: bounce rate too high',
  cap_reached: 'Daily cap reached',
}

export function pausedLabel(reason: PausedReason | null): string {
  return reason ? (PAUSED_LABELS[reason] ?? reason) : 'Running'
}

/** Percent of today's cap used, clamped 0..100 (manual sends can exceed the cap). */
export function capUsedPct(sent: number, cap: number): number {
  if (cap <= 0) return sent > 0 ? 100 : 0
  return Math.max(0, Math.min(100, Math.round((sent / cap) * 100)))
}
